import { useEffect, useRef, useState } from "react";
import { useMapState } from "contexts/MapStateContext";
import { MAPBOX_TOKEN, MAPBOX_STYLE } from "config";
import {
  addRiskMapSource,
  addRiskMapLayers,
  updateLayerVisibility,
  updateScenarioFilters,
  removeRiskMapLayers,
} from "utils/riskMap";
import { MAP_CONTAINER_STYLES } from "constants/mapStyles";
import { addMapSourcesAndLayers, BASEMAP_LAYERS } from "components/mapSetup";

const RiskComparison = ({
  compareMode,
  tileVersion,
  leftVersion,
  tilesScenario,
  tilesFilterLayers,
  isMapReady,
  onLeftMapHover,
}) => {
  const { map, mapboxGeocoderControl, basemap } = useMapState();
  const leftMapRef = useRef(null);
  const compareRef = useRef(null);
  const leftMapContainerRef = useRef(null);
  const [isLeftMapReady, setIsLeftMapReady] = useState(false);

  useEffect(() => {
    if (!compareMode) {
      if (compareRef.current) {
        compareRef.current.remove();
        compareRef.current = null;
      }
      if (leftMapRef.current) {
        leftMapRef.current.remove();
        leftMapRef.current = null;
      }
      if (leftMapContainerRef.current) {
        leftMapContainerRef.current.remove();
        leftMapContainerRef.current = null;
      }
      if (map) {
        const existingGeocoder = map._controls.find(
          (control) => control instanceof MapboxGeocoder
        );

        if (!existingGeocoder) {
          map.addControl(mapboxGeocoderControl, "top-right");
        }
      }
      setIsLeftMapReady(false);
      return;
    }

    if (!map || !isMapReady) return;

    const mapContainer = map.getContainer();
    const mapParent = mapContainer.parentElement;

    const leftContainer = document.createElement("div");
    leftContainer.id = "compare-left-map";
    Object.assign(leftContainer.style, MAP_CONTAINER_STYLES);
    mapParent.appendChild(leftContainer);
    leftMapContainerRef.current = leftContainer;

    const currentTop =
      mapContainer.style.top || window.getComputedStyle(mapContainer).top;
    mapContainer.setAttribute("data-original-top", currentTop);
    Object.assign(mapContainer.style, MAP_CONTAINER_STYLES);

    const center = map.getCenter();
    const zoom = map.getZoom();
    const bearing = map.getBearing();
    const pitch = map.getPitch();

    const leftMap = new mapboxgl.Map({
      container: leftContainer,
      style: MAPBOX_STYLE,
      accessToken: MAPBOX_TOKEN,
      center: [center.lng, center.lat],
      zoom,
      bearing,
      pitch,
      doubleClickZoom: false,
    });
    leftMap.addControl(new mapboxgl.NavigationControl(), "top-left");
    leftMap.addControl(
      new mapboxgl.ScaleControl({ maxWidth: 100, unit: "imperial" }),
      "bottom-left"
    );

    leftMapRef.current = leftMap;

    leftMap.on("load", () => {
      addMapSourcesAndLayers(leftMap);
      setIsLeftMapReady(true);
      try {
        addRiskMapSource(leftMap, leftVersion);
        addRiskMapLayers(leftMap, tilesScenario, tilesFilterLayers);
      } catch (error) {
        console.error("Error adding left map layers:", error);
      }

      const handleMouseMove = (e) => {
        const features = leftMap.queryRenderedFeatures(e.point, {
          layers: ["buildings"],
        });

        if (features.length > 0) {
          const feature = features[0];
          const rect = leftMap.getCanvas().getBoundingClientRect();
          if (onLeftMapHover) {
            onLeftMapHover({
              feature: feature.properties,
              position: { x: rect.left + e.point.x, y: rect.top + e.point.y },
            });
          }
          leftMap.getCanvas().style.cursor = "pointer";
        } else {
          if (onLeftMapHover) {
            onLeftMapHover(null);
          }
          leftMap.getCanvas().style.cursor = "";
        }
      };

      const handleMouseLeave = () => {
        if (onLeftMapHover) {
          onLeftMapHover(null);
        }
        leftMap.getCanvas().style.cursor = "";
      };

      leftMap.on("mousemove", handleMouseMove);
      leftMap.on("mouseleave", handleMouseLeave);
    });

    removeRiskMapLayers(map);
    try {
      addRiskMapSource(map, tileVersion);
      addRiskMapLayers(map, tilesScenario, tilesFilterLayers);
    } catch (error) {
      console.error("Error adding right map layers:", error);
    }

    const compare = new mapboxgl.Compare(leftMap, map, mapParent, {
      mousemove: false,
    });

    compareRef.current = compare;

    const slider = mapParent.querySelector(".mapboxgl-compare");
    if (slider) {
      slider.style.userSelect = "none";
      slider.style.webkitUserSelect = "none";

      const cleanupSelection = () => {
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        if (window.getSelection) {
          window.getSelection().removeAllRanges();
        }
      };

      slider.addEventListener("mousedown", () => {
        document.body.style.userSelect = "none";
      });

      slider.addEventListener("mouseup", cleanupSelection);
      slider.addEventListener("touchend", cleanupSelection);

      document.addEventListener("mouseup", cleanupSelection);
    }

    mapParent.querySelectorAll(".mapboxgl-ctrl").forEach((ctrl) => {
      ctrl.style.userSelect = "none";
      ctrl.style.webkitUserSelect = "none";
      ctrl.style.pointerEvents = "auto";
    });

    return () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      }

      if (compareRef.current) {
        compareRef.current.remove();
        compareRef.current = null;
      }
      if (leftMapRef.current) {
        leftMapRef.current.remove();
        leftMapRef.current = null;
      }
      if (
        leftMapContainerRef.current &&
        leftMapContainerRef.current.parentElement
      ) {
        leftMapContainerRef.current.parentElement.removeChild(
          leftMapContainerRef.current
        );
        leftMapContainerRef.current = null;
      }
      const originalTop = mapContainer.getAttribute("data-original-top");
      if (originalTop) {
        mapContainer.style.top = originalTop;
        mapContainer.removeAttribute("data-original-top");
      }

      removeRiskMapLayers(map);
    };
  }, [compareMode, map, isMapReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!compareMode || !leftMapRef.current || !isLeftMapReady) return;

    const leftMap = leftMapRef.current;

    try {
      const sourceChanged = addRiskMapSource(leftMap, leftVersion);
      if (sourceChanged || !leftMap.getLayer("buildings")) {
        addRiskMapLayers(leftMap, tilesScenario, tilesFilterLayers);
      } else {
        updateLayerVisibility(leftMap, tilesFilterLayers);
        updateScenarioFilters(leftMap, tilesScenario);
      }
    } catch (error) {
      console.error("Error updating left map:", error);
    }
  }, [
    compareMode,
    isLeftMapReady,
    leftVersion,
    tilesScenario,
    tilesFilterLayers,
  ]);

  useEffect(() => {
    if (!compareMode || !map || !isMapReady) return;

    const existingGeocoder = map._controls.find(
      (control) => control instanceof MapboxGeocoder
    );
    if (existingGeocoder) {
      map.removeControl(existingGeocoder);
    }

    try {
      const sourceChanged = addRiskMapSource(map, tileVersion);
      if (sourceChanged || !map.getLayer("buildings")) {
        addRiskMapLayers(map, tilesScenario, tilesFilterLayers);
      } else {
        updateLayerVisibility(map, tilesFilterLayers);
        updateScenarioFilters(map, tilesScenario);
      }
    } catch (error) {
      console.error("Error updating right map:", error);
    }
  }, [
    compareMode,
    isMapReady,
    tileVersion,
    tilesScenario,
    tilesFilterLayers,
    map,
  ]);

  useEffect(() => {
    if (!compareMode || !leftMapRef.current || !isLeftMapReady || !map) return;

    const leftMap = leftMapRef.current;
    const layers = Object.keys(BASEMAP_LAYERS);

    const setLayerVisibility = (targetMap, visibleLayer) => {
      layers.forEach((layer) => {
        const layerDict = BASEMAP_LAYERS[layer];
        if (layerDict.type !== "featureService" && targetMap.getLayer(layer)) {
          targetMap.setLayoutProperty(
            layer,
            "visibility",
            layer === visibleLayer ? "visible" : "none"
          );
        }
      });
    };

    if (basemap === "default") {
      setLayerVisibility(leftMap, null);
      setLayerVisibility(map, null);
    } else {
      setLayerVisibility(leftMap, basemap);
      setLayerVisibility(map, basemap);
    }
  }, [compareMode, isLeftMapReady, basemap, map]);

  return null;
};

export default RiskComparison;
